/** @format */

import {
  faAward,
  faBadgeCheck,
  faBlockQuestion,
  faQuestionCircle,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete,
  Box,
  CircularProgress,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../../api.ts';
import NewTooltip from '../../../misc/NewTooltip';

function ProductSearchAutocomplete({
  onChange,
  label = 'Rechercher un produit...',
  defaultValue = null,
  autoFocus = false,
  ...rest
}) {
  // Add the original mapAnswers function from ProductWizardDialog
  const mapAnswers = (r) => ({
    ...r,
    id: r.id || r.slug,
    answer: r.prefixed_internal_name,
    icon: r.wizard_icon || r.icon,
    icon_badge: r.wizard_icon_badge || r.icon_badge,
    color: r.wizard_color || r.color,
  });

  // Memoized mapped defaultValue
  const mappedDefaultValue = useMemo(
    () => (defaultValue ? mapAnswers(defaultValue) : null),
    [defaultValue],
  );

  // Use mappedDefaultValue in useState and useEffect
  const [searchBarValue, setSearchBarValue] = useState(mappedDefaultValue);
  const [searchBarInput, setSearchBarInput] = useState('');
  const [isSearchBarLoading, setIsSearchBarLoading] = useState(false);
  const [searchBarOptions, setSearchBarOptions] = useState([]);
  const searchTimeout = useRef(null);

  useEffect(() => {
    setSearchBarValue(mappedDefaultValue);
  }, [mappedDefaultValue]);

  // Replace loadSearchBarProducts with the original, adapted for component state
  const loadSearchBarProducts = useCallback((search) => {
    setIsSearchBarLoading(true);
    return api
      .get(
        `/products/v2/products/?limit=50&ordering=-is_validated,_indexed_internal_name&search=${search}`,
      )
      .then((result) => {
        const productsList = result.data.results.map(mapAnswers);
        // Group products by typology
        const groupedProducts = productsList.reduce((acc, product) => {
          const typologyName = product.typology?.name || 'Autre';
          const typologySlug = product.typology?.slug || 'autre';
          const key = `${typologySlug}|${typologyName}`;
          if (!acc[key]) {
            acc[key] = {
              slug: typologySlug,
              name: typologyName,
              products: [],
            };
          }
          acc[key].products.push(product);
          return acc;
        }, {});
        // Convert to array and sort by typology slug
        const sortedGroups = Object.values(groupedProducts)
          .sort((a, b) => a.slug.localeCompare(b.slug))
          .map((group) => ({
            group: group.name,
            slug: group.slug,
            products: group.products,
          }));
        setSearchBarOptions(sortedGroups);
        return sortedGroups;
      })
      .catch(() => {
        setSearchBarOptions([]);
        return [];
      })
      .finally(() => setIsSearchBarLoading(false));
  }, []);

  // After grouping and sorting, ensure defaultValue is included in the options
  useEffect(() => {
    if (!mappedDefaultValue) return;
    if (!mappedDefaultValue.id) return;
    // Check if defaultValue is already present
    const alreadyPresent = searchBarOptions.some((group) =>
      group.products.some((p) => p.id === mappedDefaultValue.id),
    );
    if (alreadyPresent) return;
    // Find typology group
    const typologyName = mappedDefaultValue.typology?.name || 'Autre';
    const typologySlug = mappedDefaultValue.typology?.slug || 'autre';
    const key = `${typologySlug}|${typologyName}`;
    // Find group index
    const groupIndex = searchBarOptions.findIndex(
      (g) => g.group === typologyName,
    );
    const newOptions = [...searchBarOptions];
    if (groupIndex === -1) {
      // Add new group
      newOptions.push({
        group: typologyName,
        slug: typologySlug,
        products: [mappedDefaultValue],
      });
    } else {
      // Add to existing group
      newOptions[groupIndex] = {
        ...newOptions[groupIndex],
        products: [mappedDefaultValue, ...newOptions[groupIndex].products],
      };
    }
    setSearchBarOptions(newOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedDefaultValue]);

  // Handle input change (debounced search)
  const handleSearchBarInputChange = (event, value, reason) => {
    setSearchBarInput(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (reason === 'input' && value) {
      searchTimeout.current = setTimeout(() => {
        loadSearchBarProducts(value);
      }, 300);
    } else if (!value) {
      setSearchBarOptions([]);
    }
  };

  // Handle selection change
  const handleSearchBarChange = (event, newValue) => {
    setSearchBarValue(newValue);
    if (onChange) onChange(newValue);
  };

  return (
    <Autocomplete
      value={searchBarValue}
      onChange={handleSearchBarChange}
      onInputChange={handleSearchBarInputChange}
      options={searchBarOptions.flatMap((group) => group.products)}
      getOptionLabel={(option) => option.answer || ''}
      loading={isSearchBarLoading}
      filterOptions={(x) => x}
      noOptionsText={
        isSearchBarLoading ? (
          <CircularProgress size={20} />
        ) : (
          'Aucun produit trouvé'
        )
      }
      groupBy={(option) => {
        const group = searchBarOptions.find((g) =>
          g.products.some((p) => p.id === option.id),
        );
        return group?.group || 'Autre';
      }}
      renderGroup={(params) => (
        <Box key={params.key}>
          <ListSubheader
            sx={{
              backgroundColor: 'background.paper',
              fontWeight: 'bold',
              fontSize: '16px',
              textTransform: 'uppercase',
              color: 'primary.main',
              height: '35px',
              mt: 0.5,
              mb: 0.5,
            }}
          >
            {params.group}
          </ListSubheader>
          {params.children}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          autoFocus={autoFocus}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {/* Badge before the name */}
            <NewTooltip
              content={
                option.is_validated ? (
                  <span>
                    Produit vérifié&nbsp;
                    <FontAwesomeIcon icon={faBadgeCheck} />
                  </span>
                ) : (
                  <span>
                    Produit non vérifié&nbsp;
                    <FontAwesomeIcon icon={faQuestionCircle} />
                  </span>
                )
              }
              placement="top"
              color={option.is_validated ? 'primary' : 'default'}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  mr: 1,
                  color: option.is_validated ? 'primary.main' : 'gray.600',
                }}
              >
                <FontAwesomeIcon
                  icon={option.is_validated ? faAward : faBlockQuestion}
                />
              </Box>
            </NewTooltip>
            <Typography variant="body1" sx={{ flexGrow: 1 }}>
              {option.answer}
            </Typography>
            {option.public_name && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', ml: 1 }}
              >
                — {option.public_name}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {...rest}
    />
  );
}

ProductSearchAutocomplete.propTypes = {
  onChange: PropTypes.func,
  label: PropTypes.string,
  defaultValue: PropTypes.any,
  autoFocus: PropTypes.bool,
};

ProductSearchAutocomplete.defaultProps = {
  onChange: null,
  label: 'Rechercher un produit...',
  defaultValue: null,
  autoFocus: false,
};

export default ProductSearchAutocomplete;
